/**
 * @file Purchase PPD Options Modal component.
 */

import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Button from '../global/Button';
import { getAlbumPrice } from '../../util/purchase-album';
import { formatPriceForDisplay } from '../../util/format-money';
import {
  CUSTOM_PRINT_DESC_MODAL,
  PPD_PURCHASE_PURCHASE_OPTIONS,
} from '../../constants/modals';
import {
  getAlbumCustomPrint,
  getAlbumCustomPrintName,
  getAlbumCustomPrintPreviewImageUrl,
  getAlbumCustomPrintBundlePrice,
  getAlbumCustomPrintPrice,
} from '../../util/custom-prints';
import CustomPrintDescriptionModal from './CustomPrintDescriptionModal';
import ContextualString from '../contextual/ContextualString';
import withModal from '../global/modal/withModal';
import CustomControl from '../global/CustomControl';
import { CUSTOM_PRINT_TYPE } from '../../constants/pro-custom-prints';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    default_price_per_photo: PropTypes.number,
    is_demo: PropTypes.bool.isRequired,
    album_folder: PropTypes.shape({
      name: PropTypes.string,
    }),
    subscription_album_folder: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.shape({
        value: PropTypes.number,
      }),
    }),
    creator: PropTypes.object,
  }),
  match: PropTypes.shape({
    url: PropTypes.string,
  }),
  hasPpaMode: PropTypes.bool,
  openModal: PropTypes.func.isRequired,
  price: PropTypes.shape({
    total_price: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.string,
    }),
    total_shipping_cost: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.string,
    }),
    total_tax: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.string,
    }),
  }),
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  user: PropTypes.shape({
    id: PropTypes.string,
    purchased_print_packages: PropTypes.shape({
      total: PropTypes.number,
    }),
  }),
  selectAlbumCustomPrint: PropTypes.func.isRequired,
  activeIdentity: PropTypes.shape({
    id: PropTypes.string,
  }),
  isActiveIdentityPurchased: PropTypes.bool,
  isActiveIdentitySelectedForSubscription: PropTypes.bool,
  activeIdentityHasCustomPrint: PropTypes.bool,
  toggleIdentityForSubscription: PropTypes.func,
  toggleIdentityForCustomPrintPurchase: PropTypes.func,
};

const PurchasePpdOptionsModal = ({
  album,
  hasPpaMode,
  openModal,
  activeIdentity,
  selectAlbumCustomPrint,
  isActiveIdentityPurchased,
  isActiveIdentitySelectedForSubscription,
  activeIdentityHasCustomPrint,
  toggleIdentityForCustomPrintPurchase,
  toggleIdentityForSubscription,
}) => {
  const [purchaseBundle, setPurchaseBundle] = useState(
    activeIdentityHasCustomPrint &&
      (isActiveIdentityPurchased || isActiveIdentitySelectedForSubscription),
  );

  useEffect(() => {
    if (
      (!purchaseBundle && activeIdentityHasCustomPrint) ||
      (purchaseBundle &&
        !activeIdentityHasCustomPrint &&
        (isActiveIdentityPurchased || isActiveIdentitySelectedForSubscription))
    ) {
      toggleIdentityForCustomPrintPurchase();
    }
  }, [purchaseBundle]);

  const albumCustomPrint = getAlbumCustomPrint(album);
  const albumCustomPrintName = getAlbumCustomPrintName(albumCustomPrint);
  const albumCustomPrintPreviewImageUrl = getAlbumCustomPrintPreviewImageUrl(
    albumCustomPrint,
  );

  const price = getAlbumPrice(album);
  const albumCustomPrintPrice = getAlbumCustomPrintPrice(albumCustomPrint);
  const albumCustomPrintBundledPrice = getAlbumCustomPrintBundlePrice(
    albumCustomPrint,
  );

  const isCollage =
    albumCustomPrint?.custom_print?.type === CUSTOM_PRINT_TYPE.COLLAGE;

  return (
    <Fragment>
      {hasPpaMode && !isActiveIdentityPurchased && activeIdentity && (
        <Fragment>
          <p className="text-extra-bold text-dark text-md mb-0">
            <ContextualString
              id="purchaseSubscriptionText"
              type={album.album_type}
              subtype={album.album_subtype}
              defaultMessage="all my photos"
            />
          </p>
          <p className="text-sm mb-2">
            Get all your photos from {album.name} for one price!
          </p>
          {hasPpaMode &&
            !isActiveIdentityPurchased &&
            activeIdentity &&
            albumCustomPrint &&
            albumCustomPrintBundledPrice !== albumCustomPrintPrice &&
            albumCustomPrintBundledPrice != null && (
              <CustomControl
                id="album-custom-print-bundle"
                className="mb-2 text-dark text-extra-bold"
                type="checkbox"
                size="sm"
                checked={purchaseBundle}
                readOnly
                label={`Include ${albumCustomPrintName} for $${formatPriceForDisplay(
                  albumCustomPrintBundledPrice,
                  true,
                )}`}
                onChange={() =>
                  setPurchaseBundle(
                    purchaseBundleCurrent => !purchaseBundleCurrent,
                  )
                }
              />
            )}
          <Button
            key={`btn-sub-${activeIdentity.id}`}
            btnBlock
            btnStyle="yellow"
            className="text-sm px-1 text-extra-bold"
            onClick={() => {
              toggleIdentityForSubscription();

              if (!isActiveIdentitySelectedForSubscription) {
                if (!activeIdentityHasCustomPrint && purchaseBundle) {
                  toggleIdentityForCustomPrintPurchase();
                }

                if (activeIdentityHasCustomPrint && !purchaseBundle) {
                  setPurchaseBundle(true);
                }
              } else {
                setPurchaseBundle(false);
              }
            }}
          >
            {isActiveIdentitySelectedForSubscription ? (
              <span>Remove from cart</span>
            ) : (
              <span>Add to cart (${formatPriceForDisplay(price, true)})</span>
            )}
          </Button>
        </Fragment>
      )}

      {albumCustomPrint && (
        <Fragment>
          <div className="d-flex mt-4">
            <p className="text-extra-bold text-dark text-md mb-0">
              {albumCustomPrintName}
            </p>
            {albumCustomPrint && albumCustomPrintPreviewImageUrl && (
              <Button
                key={`btn-custom-print-${activeIdentity.id}`}
                btnSize="xs"
                btnStyle="link"
                className="ml-auto "
                onClick={() => {
                  openModal(CUSTOM_PRINT_DESC_MODAL);
                }}
              >
                See a preview
              </Button>
            )}
          </div>
          {isCollage ? (
            <p className="text-sm">
              Create your own custom collage with your 5 favorite photos!
            </p>
          ) : (
            <p className="text-sm">
              Create your own custom print with some of your favorite photos!
            </p>
          )}
          <Button
            key={`btn-custom-print-${activeIdentity.id}`}
            btnBlock
            btnStyle="primary"
            btnOutline
            className="text-sm px-1 text-extra-bold"
            data-testid="btn-custom-print-remove"
            onClick={() => {
              if (
                albumCustomPrintPreviewImageUrl &&
                !activeIdentityHasCustomPrint
              ) {
                openModal(CUSTOM_PRINT_DESC_MODAL);
              } else {
                toggleIdentityForCustomPrintPurchase();

                if (
                  (isActiveIdentityPurchased ||
                    isActiveIdentitySelectedForSubscription) &&
                  !activeIdentityHasCustomPrint
                ) {
                  setPurchaseBundle(true);
                }
              }

              if (activeIdentityHasCustomPrint) {
                setPurchaseBundle(false);
              }
            }}
          >
            {activeIdentityHasCustomPrint
              ? `Remove from cart`
              : `Add to cart ($${formatPriceForDisplay(
                  albumCustomPrintPrice,
                  true,
                )})`}
          </Button>

          <CustomPrintDescriptionModal
            album={album}
            albumCustomPrint={albumCustomPrint}
            onCustomPrintPurchase={() =>
              selectAlbumCustomPrint(activeIdentity, albumCustomPrint)
            }
            shouldDiscount={isActiveIdentityPurchased}
          />
        </Fragment>
      )}
    </Fragment>
  );
};

PurchasePpdOptionsModal.propTypes = propTypes;

export { PurchasePpdOptionsModal as PurchasePpdOptionsModalComp };

export default withModal(PurchasePpdOptionsModal, {
  name: PPD_PURCHASE_PURCHASE_OPTIONS,
  contentLabel: 'Purchasing Options',
  size: 'sm',
});
